<template>
  <div class="img-editor">
    <template v-if="activeFile && !fileExceedsMaxSize(activeFile)">
      
      <!-- Editor Overlay Header -->
      <div class="editor-header-overlay">
        <div class="editor-info">
          {{ $t('lbl_EMPBE_Uploader_EditingImage', { aspectRatioStr: roundedAspectRatioStr }) }}
        </div>
        <button 
          type="button"
          @click="handleMakeCover"
          class="cover-btn"
          :class="isCover ? 'active' : ''"
          v-if="isShowCover"
        >
          <span class="material-symbols-outlined" style="font-size: 18px; font-variation-settings: 'FILL' 1;">star</span>
          {{ $t('lbl_EMPBE_Uploader_CoverPhoto') }}
        </button>
      </div>

      <!-- Canvas -->
      <div ref="previewRef" class="editor-canvas">
        <custom-cropper
          ref="cropperRef"
          :key="activeFile.id"
          :image-src="activeFile.previewSrc"
          :edit-state="activeFile.editState"
          :aspect-ratio="parsedAspectRatio"
          :top-padding="topPadding"
          :fit-mode="isContainMode ? 'contain' : 'cover'"
          :background-color="backgroundColor"
          :round-border="roundBorder"
          @update="handleEditorUpdate"
          @minZoomChange="handleMinZoomChange"
        />
      </div>

      <!-- Floating Toolbar -->
      <div class="editor-canvas-toolbar">
        <!-- Zoom -->
        <div class="zoom-group">
          <span class="material-symbols-outlined icon-zoom" style="font-size: 2rem;">zoom_in</span>
          <input
            type="range"
            :min="currentMinZoom"
            max="3"
            step="0.01"
            :value="activeFile.editState.zoom"
            @input="handleZoomInput"
            class="range-input"
          />
          <span class="zoom-value">{{ displayedZoomValue }}%</span>
        </div>
        <!-- Rotate Controls -->
        <div class="rotate-group">
          <button type="button" @click="rotateLeft" class="tool-btn">
            <span class="material-symbols-outlined" style="font-size: 20px;">rotate_90_degrees_ccw</span>
          </button>
          <button type="button" @click="rotateRight" class="tool-btn">
            <span class="material-symbols-outlined" style="font-size: 20px;">rotate_90_degrees_cw</span>
          </button>
        </div>
        <!-- Fit Mode Toggle -->
        <div class="separator"></div>
        <button   
          type="button" 
          @click="toggleFitMode" 
          class="fit-mode-btn tool-btn"
          :class="{ 'active': isContainMode }"
          :title="isContainMode ? 'Fit to contain' : 'Fit to cover'"
        >
          <span class="material-symbols-outlined" style="font-size: 20px;">
            fit_screen 
          </span>
          <span>{{ $t('lbl_EMPBE_Uploader_ScaleToFit') }}</span>
        </button>
        <div class="separator"></div>
        <input type="color" v-model="backgroundColor" class="color-picker-input"/>
        <div class="separator"></div>
        <!-- Reset -->
        <button type="button" @click="resetEditor" class="reset-btn">
          {{ $t('lbl_EMPBE_Uploader_Reset') }}
        </button>

      </div>

    </template>
  </div>
</template>

<script>
import CustomCropper from './imgCropper.vue'; 

export default {
  name: 'ImgEditor',
  components: {
    CustomCropper
  },
  props: {
    activeFile: {
      type: Object,
      default: null
    },
    aspectRatioStr: {
      type: String,
      default: "1:1"
    },
    maxSize: {
      type: Number,
      default: 500
    },
    isCover: {
      type: Boolean,
      default: false
    },
    isShowCover: {
      type: Boolean,
      default: false
    },
    roundBorder: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      currentMinZoom: 0.5,
      containerHeight: 0,
      containerWidth: 0,
      calculatedTopPadding: 12,
      backgroundColor: '#ffffff'

    };
  },
  computed: {
    roundedAspectRatioStr() {
      const parts = this.aspectRatioStr.split(':').map(Number);
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1]) && parts[1] !== 0) {
        return `${Math.round(parts[0])}:${Math.round(parts[1])}`;
      }
      return this.aspectRatioStr;
    },
    isContainMode() {
      return this.activeFile?.editState?.fitMode === 'contain';
    },
    parsedAspectRatio() {
      const parts = this.aspectRatioStr.split(':').map(Number);
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1]) && parts[1] !== 0) {
        return parts[0] / parts[1];
      }
      return 1;
    },
    displayedZoomValue() {
      if (!this.activeFile) return 0;
      const zoom = this.activeFile.editState.zoom;
      const minZoom = this.currentMinZoom;
      const maxZoom = 3;
      const maxDisplay = 300 - (minZoom * 100);
      
      // Normalize zoom value from [minZoom, maxZoom] to [0, maxDisplay]
      const normalized = ((zoom - minZoom) / (maxZoom - minZoom)) * maxDisplay;
      return Math.round(Math.max(0, normalized));
    },
    topPadding() {
      return this.calculatedTopPadding;
    }
  },
  watch: {
    activeFile() {
      this.currentMinZoom = 0.5;
      this.updateContainerHeight();
      // Initialize backgroundColor from editState if available
      if (this.activeFile?.editState?.backgroundColor) {
        this.backgroundColor = this.activeFile.editState.backgroundColor;
      }
    },
    parsedAspectRatio() {
      this.updateContainerHeight();
      // Also recalculate immediately if dimensions are already available
      if (this.containerWidth > 0 && this.containerHeight > 0) {
        this.calculateTopPadding(this.containerWidth, this.containerHeight);
      }
    },
    backgroundColor(newColor) {
      if (this.activeFile?.editState) {
        this.handleEditorUpdate({ backgroundColor: newColor });
      }
    }
  },
  mounted() {
    this.updateContainerHeight();
    window.addEventListener('resize', this.updateContainerHeight);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateContainerHeight);
  },
  methods: {
    fileExceedsMaxSize(file) {
      if (!file) return false;
      const maxSizeBytes = this.maxSize * 1024 * 1024;
      const fileSizeBytes = file.sizeBytes || 0;
      return fileSizeBytes > maxSizeBytes;
    },
    handleEditorUpdate(updates) {
      const { isFirstInit, ...updatedState } = updates;
      updatedState.isDirty = !(isFirstInit || false);
      this.$emit('update', updatedState);
    },
    handleMinZoomChange(val) {
      this.currentMinZoom = val;
      this.$emit('minZoomChange', val);
    },
    handleZoomInput(e) {
      let val = Number(e.target.value);
      if (val < this.currentMinZoom) val = this.currentMinZoom;
      this.handleEditorUpdate({ zoom: val });
    },
    resetEditor() {
      if (!this.activeFile) return;
      this.backgroundColor = '#ffffff';
      this.handleEditorUpdate({ 
        crop: { x: 0, y: 0 }, 
        zoom: this.currentMinZoom, 
        rotation: 0, 
        fitMode: 'cover', 
        backgroundColor: this.backgroundColor,
        isDirty: false
      });
    },
    rotateRight() {
      if (!this.activeFile) return;
      this.handleEditorUpdate({ rotation: (this.activeFile.editState.rotation + 90) % 360 });
    },
    rotateLeft() {
      if (!this.activeFile) return;
      let newRot = (this.activeFile.editState.rotation - 90) % 360;
      if (newRot < 0) newRot += 360;
      this.handleEditorUpdate({ rotation: newRot });
    },
    handleMakeCover() {
      this.$emit('make-cover');
    },
    updateContainerHeight() {
      this.$nextTick(() => {
        if (this.$refs.previewRef) {
          const rect = this.$refs.previewRef.getBoundingClientRect();
          this.containerHeight = rect.height;
          this.containerWidth = rect.width;
          this.calculateTopPadding(rect.width, rect.height);
        }
      });
    },
    calculateTopPadding(width, height) {
      if (this.parsedAspectRatio > 1 && width > 0 && height > 0) {
        // Center the grid vertically when aspect ratio > 1
        const padding = height < 200 ? 0 : 100;
        const availW = width - padding;
        const availH = height - padding;
        
        // Calculate crop box dimensions (matching cropper logic)
        let w = availW;
        let h = w / this.parsedAspectRatio;
        
        if (h > availH) {
          h = availH;
          w = h * this.parsedAspectRatio;
        }
        
        // Center vertically
        const centeredTop = (height - h) / 2;
        this.calculatedTopPadding = Math.max(0, centeredTop);
      } else {
        this.calculatedTopPadding = 12;
      }
    },
    toggleFitMode() {
      const newMode = this.isContainMode ? 'cover' : 'contain';
      this.handleEditorUpdate({ fitMode: newMode });
    }
  }
};
</script>

<style scoped>
.img-editor {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.editor-header-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(to bottom, rgba(0,0,0,0.5), transparent);
  pointer-events: none;
}

.editor-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0,0,0,0.5);
}

.cover-btn {
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s;
  border: none;
  background-color: rgba(0, 0, 0, 0.40);
  color: white;
  cursor: pointer;
}
.cover-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}
.cover-btn.active {
  background-color: #2563eb;
}

.editor-canvas {
  flex: 1;
  position: relative;
  overflow: hidden;
}

/* Editor Toolbar */
.editor-canvas-toolbar {
  display: flex;
  width: max-content;
  padding: 0.75rem 1rem 0.75rem 1.5rem;
  align-items: center;
  gap: 0.75rem;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 1rem;
  border-radius: 62.4375rem;
  background: #fff;
}

.zoom-group {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1 0 0;
}

.icon-zoom {
  color: black;
  opacity: 0.6;
}

.zoom-value {
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  width: 2rem;
  text-align: right;
}

.separator {
  width: 1px;
  height: 2rem;
  background-color: #e2e8f0;
}

.rotate-group {
  display: flex;
  align-items: center;
}

  .rotate-group .tool-btn {
    padding: 0.25rem;
  }

.fit-mode-btn.tool-btn {
  padding:4px;
  border-radius: 4px;
}
.tool-btn {
  padding: 0.5rem;
  color: #64748b;
  border-radius: 9999px;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  gap: 4px;
}
.tool-btn:hover {

  background-color: #eef2ff;
  border-color: #e0e7ff;
}
.tool-btn.active {
  color: #4f46e5;

}

.reset-btn {
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.2s;
}
.reset-btn:hover {
  color: #4f46e5;
}

/* Color Picker Input Styling */
.color-picker-input {
  width: 26px;
  height: 26px;
  border: 1.5px solid rgba(0, 0, 0, 0.40);
  border-radius: 50%;
  cursor: pointer;
  padding: 3px;
  overflow: hidden;
  -webkit-appearance: none;
  appearance: none;
}

.color-picker-input::-webkit-color-swatch-wrapper {
  padding: 0;
}

.color-picker-input::-webkit-color-swatch {
  border: none;
  border-radius: 50%;
}

.color-picker-input::-moz-color-swatch {
  border: none;
  border-radius: 50%;
}

/* Range Input Customization */
.range-input {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  width: 100%;
}
.range-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  height: 16px;
  width: 16px;
  border-radius: 50%;
  background: #6366f1;
  cursor: pointer;
  margin-top: -6px;
  box-shadow: 0 0 0 2px white;
}
.range-input::-webkit-slider-runnable-track {
  width: 100%;
  height: 4px;
  cursor: pointer;
  background: #e2e8f0;
  border-radius: 2px;
}

.material-symbols-outlined {
  font-family: 'Material Symbols Outlined';
  font-weight: normal;
  font-style: normal;
  font-size: 24px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-feature-settings: 'liga';
  font-feature-settings: 'liga';
  -webkit-font-smoothing: antialiased;
}
</style>

